import React, { useEffect, useState } from 'react';
import { Table, Tag, Switch, Button, Modal, Select, Space, Typography, Input, Skeleton, Tooltip } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { toast } from '../common/Popover';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import CommonLayout from '../common/LayoutBlock';
import { handleApiError } from '../utils/errorHandler';
import { useAuthOrg } from '../context/AuthOrgContext';
import eventMappingService, { ClientRow, DataSourceEvent, MappingPayload } from '../apiServices/eventMappingService';
import '../assets/css/tablesShadow.css'

const { Title, Text } = Typography;
const { Option } = Select;

const PAGE_SIZE = 10;

const MetaConversionEvents: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ClientRow[]>([]);
  const [dataSourceEvents, setDataSourceEvents] = useState<DataSourceEvent[]>([]);
  const [searchText, setSearchText] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientRow | null>(null);
  const [mappingRows, setMappingRows] = useState<
    {
      dataSourceEvent: string | null;
      botEvent: string | null;
      botEventId: string | null;
      value?: number;
      currency?: string;
      valueType?: string;
      valueMin?: number;
      valueMax?: number;
    }[]
  >([
    { dataSourceEvent: null, botEvent: null, botEventId: null, valueType: 'static', currency: 'INR' },
    { dataSourceEvent: null, botEvent: null, botEventId: null, valueType: 'static', currency: 'INR' },
  ]);
  const [saving, setSaving] = useState(false);
  const { setBreadcrumbs } = useBreadcrumbs();
  const { currentOrg, availableOrgs } = useAuthOrg();
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(currentOrg?.orgId || null);


  useEffect(() => {
    setBreadcrumbs([
      { title: <div className="text-lg">Conversion Event Mapping</div> },
    ]);
  }, [setBreadcrumbs]);

  // Fetch org-wide options
  useEffect(() => {
    setSearchText('');
    const fetchOrgOptions = async () => {
      // if (!currentOrg?.orgId) return;
      try {
        setLoading(true);
        const res = await eventMappingService.fetchOrgOptions(currentOrgId!);

        if (res.meta.status) {
          const { dataSourceEvents, clients } = res.data;

          // Ensure 'View Content' is present
          const viewContentExists = dataSourceEvents.some(e => e.name === 'View Content');
          if (!viewContentExists) {
            dataSourceEvents.push({ id: -1, name: 'View Content' });
          }

          setDataSourceEvents(dataSourceEvents);
          setData(clients);
        } else {
          toast('error', res.meta.message);
        }
      } catch (error) {
        handleApiError(error, 'conversion-events-fetch', 'Failed to load conversion mapping data');
        toast('error', 'Failed to load conversion mapping data');
      } finally {
        setLoading(false);
      }
    };

    fetchOrgOptions();
  }, [currentOrgId]);

  // Filter data
  const filteredData = data.filter(
    (item) =>
      item?.subdomain?.toLowerCase()?.includes(searchText.toLowerCase()) ||
      item?.wabaNumber?.includes(searchText)
  );

  // Toggle Conversion API
  const toggleConversionApi = async (clientId: string, enabled: boolean) => {
    try {
      const res = await eventMappingService.toggleConversionApi(clientId, enabled);

      if (res.meta.status) {
        setData((prev) =>
          prev.map((c) =>
            c.clientId === clientId ? { ...c, conversionApiEnabled: enabled } : c
          )
        );
        toast('success', `Conversion API ${enabled ? 'enabled' : 'disabled'}`);
      } else {
        toast('error', res.meta.message);
      }
    } catch (error) {
      toast('error', 'Failed to update Conversion API');
    }
  };

  // Open Modal
  const openMappingModal = (client: ClientRow) => {
    setSelectedClient(client);
    const initialRows =
      client.mappedEvents.length > 0
        ? client.mappedEvents.map((m) => ({
          dataSourceEvent: m.convEventName,
          botEvent: m.milestoneName,
          botEventId: m.milestoneId,
          value: m.value,
          currency: m.currency || 'INR',
          valueType: 'static', // Default to static for existing
        }))
        : [
          { dataSourceEvent: null, botEvent: null, botEventId: null, valueType: 'static', currency: 'INR' },
          { dataSourceEvent: null, botEvent: null, botEventId: null, valueType: 'static', currency: 'INR' },
        ];
    setMappingRows(initialRows);
    setModalOpen(true);
  };

  // Add/Remove Row
  const addRow = () => {
    setMappingRows([...mappingRows, { dataSourceEvent: null, botEvent: null, botEventId: null, valueType: 'static', currency: 'INR' }]);
  };

  const removeRow = () => {
    if (mappingRows.length > 1) {
      setMappingRows(mappingRows.slice(0, -1));
    }
  };

  const updateRow = (index: number, field: string, value: any) => {
    const updated = [...mappingRows];
    (updated[index] as any)[field] = value;
    setMappingRows(updated);
  };

  // Save Mapping
  const saveMapping = async () => {
    if (!selectedClient) return;

    const validRows = mappingRows.filter((r) => r.dataSourceEvent && r.botEvent && r.botEventId);
    if (validRows.length !== mappingRows.length) {
      toast('error', 'Please fill all event mappings');
      return;
    }

    const payload: MappingPayload = {
      data: validRows.map((r) => ({
        event_name: r.dataSourceEvent!,
        milestone_name: r.botEvent!,
        milestone_id: r.botEventId!,
        value: r.value,
        currency: r.currency,
        value_type: r.valueType,
        value_min: r.valueMin,
        value_max: r.valueMax,
      })),
    };

    try {
      setSaving(true);
      const res = await eventMappingService.saveEventMapping(selectedClient.clientId, payload);

      if (res.meta.status) {
        toast('success', 'Event mapping saved successfully');
        setData((prev) =>
          prev.map((c) =>
            c.clientId === selectedClient.clientId
              ? {
                ...c,
                mappedEvents: payload.data.map((d) => ({
                  convEventName: d.event_name,
                  milestoneName: d.milestone_name,
                  milestoneId: d.milestone_id,
                  milestonePositioner: '', // These fields are not in the payload but required by MappedEvent type
                  value: 0,
                  currency: ''
                }))
              }
              : c
          )
        );
        setModalOpen(false);
      } else {
        toast('error', res.meta.message);
      }
    } catch (error) {
      toast('error', 'Failed to save event mapping');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'WABA Domain',
      dataIndex: 'subdomain',
      key: 'subdomain',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'WABA Number',
      dataIndex: 'wabaNumber',
      key: 'waba',
    },
    {
      title: 'Bot Name',
      dataIndex: 'botName',
      key: 'botName',
      render: (text: string | null) => text || <Text type="secondary">—</Text>,
    },
    {
      title: 'Bot Status',
      key: 'status',
      render: (_: any, record: ClientRow) => (
        <Tag color={record.botId ? 'green' : 'red'}>
          {record.botId ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Conversion Event',
      key: 'conversion',
      render: (_: any, record: ClientRow) => (
        <Space>
          <Switch
            size="small"
            checked={record.conversionApiEnabled}
            disabled={!record.botId || record.milestones.length === 0}
            onChange={(checked) => toggleConversionApi(record.clientId, checked)}
          />
          {record.botId && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openMappingModal(record)}
              disabled={!record.conversionApiEnabled}
            >Event Mapping</Button>
          )}
        </Space>
      ),
    },
    {
      title: 'Mapping Status',
      key: 'mappingStatus',
      render: (_: any, record: ClientRow) => (
        <Tag color={record.conversionApiEnabled ? record.mappedEvents.length > 0 ? 'green' : 'red' : "gray"} aria-disabled={!record.conversionApiEnabled}>
          {record.conversionApiEnabled ? record.mappedEvents.length > 0 ? 'Active' : 'Inactive' : "Disabled"}
        </Tag>
      ),
    }
  ];

  const changeOrg = (orgId: string) => {
    // const org = availableOrgs.find((org) => org.orgId === orgId);
    setCurrentOrgId(orgId);
  };

  return (
    <>
      <CommonLayout>
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <Title level={4} style={{ margin: 0 }}>Meta Conversion Event Mapping</Title>
            <div>
              <Typography.Text className="text-sm text-gray-500">Account : </Typography.Text>
              <Select
                value={currentOrgId}
                onChange={changeOrg}
                style={{ width: 250 }}
                disabled={availableOrgs.length <= 1}
                placeholder="Select Account"
              >
                {availableOrgs.map((org) => (
                  <Option key={org.orgId} value={org.orgId}>
                    <Tooltip title={org.orgName}>
                      {org.orgName}
                    </Tooltip>
                  </Option>
                ))}
              </Select>
            </div>
          </div>
          <Input.Search
            placeholder="Search by client or WABA..."
            allowClear
            onSearch={setSearchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
          />
        </div>

        <div className="bg-gray-50 p-4">
          {loading ? (
            <Skeleton active />
          ) : (
            <Table
              columns={columns}
              dataSource={filteredData}
              rowKey="clientId"
              pagination={{
                pageSize: PAGE_SIZE,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} clients`,
              }}
            />
          )}
        </div>
      </CommonLayout>

      {/* Mapping Modal */}
      <Modal
        title={
          <Space>
            <span>Map Events:</span>
            <Text strong>{selectedClient?.subdomain}</Text>
            <Tag color="blue">{selectedClient?.wabaNumber}</Tag>
          </Space>
        }
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={saving}
            onClick={saveMapping}
            disabled={mappingRows.some((r) => !r.dataSourceEvent || !r.botEvent)}
          >
            Save Mapping
          </Button>,
        ]}
        width={800}
      >
        {selectedClient && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 font-semibold text-center">
              <div>Meta Data Source Event</div>
              <div>Bot Milestone</div>
            </div>

            {mappingRows.map((row, idx) => (
              <div key={idx} className="grid grid-cols-2 gap-4">
                <Select
                  placeholder="Select Data Source Event"
                  value={row.dataSourceEvent}
                  onChange={(val) => updateRow(idx, 'dataSourceEvent', val)}
                  style={{ width: '100%' }}
                  allowClear
                >
                  {dataSourceEvents.map((ev) => {
                    const isUsed = mappingRows.some(
                      (r, i) => i !== idx && r.dataSourceEvent === ev.name
                    );
                    return (
                      <Option key={ev.id} value={ev.name} disabled={isUsed}>
                        {ev.name}
                      </Option>
                    );
                  })}
                </Select>

                <Select
                  placeholder="Select Bot Milestone"
                  value={row.botEventId}
                  onChange={(val, option: any) => {
                    updateRow(idx, 'botEvent', option.children);
                    updateRow(idx, 'botEventId', val);
                  }}
                  style={{ width: '100%' }}
                  allowClear
                >
                  {selectedClient.milestones.map((m) => {
                    const isUsed = mappingRows.some(
                      (r, i) => i !== idx && r.botEventId === String(m.milestoneId)
                    );
                    return (
                      <Option key={m.milestoneId} value={String(m.milestoneId)} disabled={isUsed}>
                        {m.milestoneName}
                      </Option>
                    );
                  })}
                </Select>


                {/* Amount Input for Purchase */}
                {
                  row.dataSourceEvent === 'Purchase' && (
                    <div className="col-span-2 bg-gray-50 p-3 rounded-md mt-2">
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Space>
                          <Text strong>Value Type:</Text>
                          <Select
                            value={row.valueType}
                            onChange={(val) => updateRow(idx, 'valueType', val)}
                            style={{ width: 120 }}
                          >
                            <Option value="static">Static</Option>
                            <Option value="range">Range</Option>
                          </Select>
                          <Text strong className="ml-4">Currency:</Text>
                          <Select
                            value={row.currency}
                            onChange={(val) => updateRow(idx, 'currency', val)}
                            style={{ width: 100 }}
                          >
                            <Option value="INR">INR</Option>
                            <Option value="USD">USD</Option>
                          </Select>
                        </Space>

                        {row.valueType === 'static' ? (
                          <Space>
                            <Text>Amount:</Text>
                            <Input
                              type="number"
                              value={row.value}
                              onChange={(e) => updateRow(idx, 'value', parseFloat(e.target.value))}
                              style={{ width: 150 }}
                              placeholder="0.00"
                            />
                          </Space>
                        ) : (
                          <Space>
                            <Text>Min:</Text>
                            <Input
                              type="number"
                              value={row.valueMin}
                              onChange={(e) => updateRow(idx, 'valueMin', parseFloat(e.target.value))}
                              style={{ width: 120 }}
                              placeholder="Min"
                            />
                            <Text>Max:</Text>
                            <Input
                              type="number"
                              value={row.valueMax}
                              onChange={(e) => updateRow(idx, 'valueMax', parseFloat(e.target.value))}
                              style={{ width: 120 }}
                              placeholder="Max"
                            />
                          </Space>
                        )}
                      </Space>
                    </div>
                  )
                }
              </div>
            ))}

            <div className="flex justify-between">
              {mappingRows.length < dataSourceEvents.length && (
                <Button type="link" onClick={addRow}>
                  + Add Row
                </Button>
              )}
              {mappingRows.length > 1 && (
                <Button type="link" danger onClick={removeRow}>
                  - Remove Row
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal >
    </>
  );
};

export default MetaConversionEvents;